// src/pages/tgbot.ts —— 06 C-15 Telegram Bot 配置页定义（PAGES_TGBOT 经 pages/index 注册，后序覆盖前序）：
// 页签 0「Bot 列表」= 表格页（GET /api/admin/bots，facet owner / mode / status，sort created_at，统计卡取信封 stats）+ 表单 bot + 行内动作；
// 页签 1「当前 Bot 配置」由 dash 域原生渲染（domains/dash.tgbot.ts），本表不登记其视图（table 域在页签 1 不渲染表格区，pg.table 按页签门控）
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { AdminPage } from '../../../shared/src/api/types';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtDateTime } from '../../../shared/src/time';
import { forgetBot, selectBot, selectedBotId } from '../domains/dash.tgbot';
import { openConfirm, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_TGBOT, REWRITES_TGBOT } from '../i18n/tgbot';
import type { AgentRow } from './agents.types';
import type { BotListStats, BotPrimaryResp, BotRow, BotSaveResp } from './tgbot.types';
import { defineView, type ActionDef, type FieldDef, type FormDef, type FormVals, type Need, type PageMap, type StatVals } from './types';
import { cell, chip, chipOf, localErr, withBadge, type CellVals, type ChipKind } from './ui';

const EMPTY = '—';
const TG_EDIT: Need = { module: 'tgbot', bit: 'edit' };
const TOKEN_RE = /^\d+:[A-Za-z0-9_-]{30,}$/; // 06 C-15 / TD-915
const DEFAULT_UPDATES = 'message,callback_query,my_chat_member';
const AGENT_PAGE = 50; // 代理下拉：GET /agents?tab=all 首页 50 条（size 白名单 10 / 20 / 50；列表已排除软删代理）
const NO_AGENT = '—';
// 代理下拉尾部「仅列出前 50 个代理」提示项的选项值：固定哨兵，显示文案经 optionLabel 按实时字典渲染（文案比对在切换语言后失效）
const AGENT_CAP = '__cap__';

function txt(v: FormVals, k: string): string {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
}

function dt(ctx: AdminCtx, iso: string | null | undefined): string {
  return fmtDateTime(iso, { tz: ctx.host.state.sh.timezone || undefined });
}

function ownerMap(t: Dict): Readonly<Record<string, readonly [ChipKind, string]>> {
  return { platform: ['blue', str(t, 'ownerPlatform')], agent: ['purple', str(t, 'ownerAgent')] };
}

function modeMap(t: Dict): Readonly<Record<string, readonly [ChipKind, string]>> {
  return { webhook: ['green', str(t, 'modeWebhook')], polling: ['amber', str(t, 'modePolling')] };
}

function statusMap(t: Dict): Readonly<Record<string, readonly [ChipKind, string]>> {
  return { active: ['green', str(t, 'stOn')], disabled: ['red', str(t, 'stOff')] };
}

// facet 选项文案：未知值回落原值（P-12）
const labelOf = (m: (t: Dict) => Readonly<Record<string, readonly [ChipKind, string]>>) => (t: Dict, v: string): string => m(t)[v]?.[1] || v;

// 06 C-15 归属列：platform → 平台官方 blue；agent → {代理名} purple
function ownerCell(t: Dict, r: BotRow): CellVals {
  if (r.owner_kind === 'agent') return chip('purple', r.agent_name ?? str(t, 'ownerAgent'));
  return chipOf(ownerMap(t), r.owner_kind, 'bots.owner_kind');
}

// 06 C-15 Webhook / 事件列：webhook 模式 = 派生 URL（TD-916 未配置公网地址时为 null → 「—」）；polling = getUpdates；sub = allowed_updates
function hookText(r: BotRow): string {
  if (r.mode === 'polling') return 'getUpdates';
  return r.webhook_url ?? EMPTY;
}

// 0039 平台主 Bot：名称列右侧「主 Bot」章（title 说明影响范围）
function nameCell(t: Dict, r: BotRow, cur: boolean): CellVals {
  const c = cell(r.name, cur ? str(t, 'cSelected') : String(r.id));
  return r.is_primary === true ? withBadge(c, 'green', str(t, 'tgbPrimary'), str(t, 'tgbPrimaryScope')) : c;
}

function botCells(ctx: AdminCtx, r: BotRow): readonly CellVals[] {
  const t = ctx.t;
  const cur = selectedBotId(ctx.host) === r.id;
  return [
    nameCell(t, r, cur),
    cell(r.bot_telegram_id || EMPTY, '', true),
    cell(r.username, '', true),
    ownerCell(t, r),
    chipOf(modeMap(t), r.mode, 'bots.mode'),
    cell(hookText(r), r.allowed_updates, true),
    cell(Number.isFinite(r.via_users) ? r.via_users.toLocaleString('en-US') : EMPTY),
    cell(dt(ctx, r.created_at)),
    chipOf(statusMap(t), r.status, 'bots.status'),
  ];
}

function count(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString('en-US') : EMPTY;
}

// 统计卡三项取信封 stats（全局口径，不随筛选变化）；缺失 / 非数字 → 「—」，不以当前页数据冒充全量
function botStats(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): readonly StatVals[] {
  const t = ctx.t;
  const raw = page['stats'];
  const s: Partial<Record<keyof BotListStats, unknown>> = typeof raw === 'object' && raw !== null ? { total: Reflect.get(raw, 'total'), agent: Reflect.get(raw, 'agent'), via_users: Reflect.get(raw, 'via_users') } : {};
  return [
    { label: str(t, 'kBotTotal'), value: count(s.total), sub: str(t, 'kBotTotalSub'), dark: true },
    { label: str(t, 'kBotAgent'), value: count(s.agent), sub: str(t, 'kBotAgentSub') },
    { label: str(t, 'kBotUsers'), value: count(s.via_users), sub: str(t, 'kBotUsersSub') },
  ];
}

// ---- 表单 bot（06 C-15 表单规格）----

interface AgentChoice {
  id: number;
  label: string;
}

// GET /agents 需 agents.view（02 L315 只要求 bots 路由 tgbot.*）：仅持 tgbot.edit 的角色遇 1501 降级为空列表（下拉只剩当前代理 + 「—」）
async function agentChoices(ctx: AdminCtx): Promise<readonly AgentChoice[]> {
  try {
    const page = await ctx.gp.api.call<AdminPage<AgentRow>>('GET /api/admin/agents', { query: { tab: 'all', page: 1, size: AGENT_PAGE } });
    const rows = Array.isArray(page.list) ? page.list : [];
    return rows.map((a) => ({ id: a.id, label: `${a.biz_no} · ${a.name}` }));
  } catch (e) {
    if (e instanceof ApiError && e.code === 1501) {
      console.warn('[gp:tgbot] agents 列表无权限，代理下拉降级');
      return [];
    }
    throw e;
  }
}

// 下拉选项恒含当前行的归属代理（不在首页 50 条或无 agents.view 时补入），编辑其他字段不被 tgbErrAgent 误拦
export function choicesFor(agents: readonly AgentChoice[], r: BotRow | null): readonly AgentChoice[] {
  if (!r || r.owner_kind !== 'agent' || r.agent_id === null || agents.some((a) => a.id === r.agent_id)) return agents;
  return [...agents, { id: r.agent_id, label: r.agent_name ?? `#${r.agent_id}` }];
}

// 服务端 4220 data.field → 表单字段标签（TD-846）
function fieldLabel(t: Dict, field: string): string {
  const m: Readonly<Record<string, string>> = {
    name: 'cBotName', token: 'tgToken', bot_telegram_id: 'cBotId', username: 'cBotUser', owner_kind: 'cOwner', agent_id: 'ownerAgent',
    mode: 'cBotMode', status: 'cStatus', allowed_updates: 'tgUpdates',
  };
  const k = m[field];
  return k ? str(t, k) || field : field;
}

// 1503：webhook 模式且启用时后端需 HOOK_PUBLIC_BASE_URL 调 setWebhook，未配置即拒（TD-916）
function noPublicBase(e: unknown): boolean {
  return e instanceof ApiError && e.code === 1503;
}

// 1012 reason=primary_bot：主 Bot 不可停用 / 改归代理 / 删除（0039，须先把其他 Bot 设为主 Bot）
function isPrimaryErr(e: unknown): boolean {
  return e instanceof ApiError && e.code === 1012 && e.data.reason === 'primary_bot';
}

function saveErr(route: RouteKey, e: unknown, t: Dict): unknown {
  if (noPublicBase(e)) return localErr(route, str(t, 'tgbErrNoPublicBase'));
  if (isPrimaryErr(e)) return localErr(route, str(t, 'tgbErrPrimary'));
  if (e instanceof ApiError && e.code === 4220 && e.data.field) return localErr(route, `${interpolate(str(t, 'errCfgField'), { field: fieldLabel(t, e.data.field) })}${e.message ? ` · ${e.message}` : ''}`);
  return e;
}

// 本地校验（06 C-15 表单校验列）：Token 格式（新增必填 / 编辑留空不改）、Bot ID 数字且等于 Token 冒号前段、用户名 @ 开头、owner=agent 必选代理
export function validateBot(v: FormVals, t: Dict, editing: boolean): string {
  const token = txt(v, 'token');
  if ((!editing || token !== '') && !TOKEN_RE.test(token)) return str(t, 'tgbErrToken');
  const id = txt(v, 'bot_telegram_id');
  if (id !== '' && (!/^\d+$/.test(id) || (token !== '' && token.split(':')[0] !== id))) return str(t, 'tgbErrBotId');
  if (!txt(v, 'username').startsWith('@')) return str(t, 'tgbErrUsername');
  const agent = txt(v, 'agent');
  if (txt(v, 'owner_kind') === 'agent' && (agent === '' || agent === NO_AGENT || agent === AGENT_CAP)) return str(t, 'tgbErrAgent');
  return '';
}

type BotBody = { name: string; bot_telegram_id: string; username: string; token: string; owner_kind: string; agent_id: number | null; mode: string; allowed_updates: string; status: string };

function bodyOf(v: FormVals, agents: readonly AgentChoice[]): BotBody {
  const owner = txt(v, 'owner_kind');
  const agent = agents.find((a) => a.label === txt(v, 'agent'));
  return {
    name: txt(v, 'name'),
    bot_telegram_id: txt(v, 'bot_telegram_id'),
    username: txt(v, 'username'),
    token: txt(v, 'token'),
    owner_kind: owner,
    agent_id: owner === 'agent' ? agent?.id ?? null : null,
    mode: txt(v, 'mode'),
    allowed_updates: txt(v, 'allowed_updates'),
    status: txt(v, 'status'),
  };
}

// PUT 只提交变更键（键缺省 = 不改；token 留空 = 不改，TD-706 只含 status 时后端声明 toggle_bot）
function changedOf(next: BotBody, r: BotRow): Partial<BotBody> {
  const prev: BotBody = { name: r.name, bot_telegram_id: r.bot_telegram_id, username: r.username, token: '', owner_kind: r.owner_kind, agent_id: r.owner_kind === 'agent' ? r.agent_id : null, mode: r.mode, allowed_updates: r.allowed_updates, status: r.status };
  const out: Partial<BotBody> = {};
  for (const k of Object.keys(next) as Array<keyof BotBody>) {
    if (k === 'token' ? next.token === '' : next[k] === prev[k]) continue;
    Object.assign(out, { [k]: next[k] });
  }
  return out;
}

function hookToast(ctx: AdminCtx, r: BotSaveResp, okKey: string): void {
  const t = ctx.gp.ui.dict();
  if (r.webhook && !r.webhook.ok) ctx.gp.ui.toast(interpolate(str(t, 'tgbHookFail'), { error: r.webhook.error ?? '' }));
  else ctx.gp.ui.toast(str(t, okKey));
}

function botForm(ctx: AdminCtx, r: BotRow | null, listed: readonly AgentChoice[]): FormDef {
  const t = ctx.t;
  const editing = r !== null;
  const agents = choicesFor(listed, r);
  const agentLabel = r?.owner_kind === 'agent' && r.agent_id !== null ? agents.find((a) => a.id === r.agent_id)?.label ?? NO_AGENT : NO_AGENT;
  // 首页取满 50 条：尾部提示仅列出前 50 个（选中即按未选代理拦截）
  const capHint = listed.length >= AGENT_PAGE ? [AGENT_CAP] : [];
  const fields: readonly FieldDef[] = [
    { k: 'name', label: str(t, 'cBotName'), type: 'text' },
    { k: 'bot_telegram_id', label: str(t, 'cBotId'), type: 'text', optional: true, placeholder: '8123456789' },
    { k: 'username', label: str(t, 'cBotUser'), type: 'text', placeholder: '@my_cards_bot' },
    { k: 'token', label: str(t, 'tgToken'), type: 'password', wide: true, optional: editing },
    { k: 'owner_kind', label: str(t, 'cOwner'), type: 'select', options: ['platform', 'agent'] },
    { k: 'agent', label: str(t, 'ownerAgent'), type: 'select', options: [NO_AGENT, ...agents.map((a) => a.label), ...capHint], optionLabel: (d, o) => (o === AGENT_CAP ? str(d, 'tgbAgentCap') : o), optional: true },
    { k: 'mode', label: str(t, 'cBotMode'), type: 'select', options: ['webhook', 'polling'] },
    { k: 'allowed_updates', label: str(t, 'tgUpdates'), type: 'text', wide: true, placeholder: DEFAULT_UPDATES },
    { k: 'status', label: str(t, 'cStatus'), type: 'select', options: ['active', 'disabled'] },
  ];
  const init: FormVals = r
    ? { name: r.name, bot_telegram_id: r.bot_telegram_id, username: r.username, token: '', owner_kind: r.owner_kind, agent: agentLabel, mode: r.mode, allowed_updates: r.allowed_updates, status: r.status }
    : { name: '', bot_telegram_id: '', username: '', token: '', owner_kind: 'agent', agent: NO_AGENT, mode: 'webhook', allowed_updates: DEFAULT_UPDATES, status: 'active' };
  return {
    title: `${str(t, editing ? 'edit' : 'add')} · ${str(t, 'tgbot')}`,
    tip: [str(t, 'botIdTip'), str(t, 'botOwnerTip'), str(t, 'botProxyTip')].filter((x) => x !== '').join(' '),
    fields,
    init,
    validate: (v, d) => validateBot(v, d, editing),
    submit: async (c, v) => {
      const body = bodyOf(v, agents);
      const alive = sessionGuard(c);
      if (!r) {
        const route = 'POST /api/admin/bots';
        let res: BotSaveResp;
        try {
          res = await c.gp.api.call<BotSaveResp>(route, { body });
        } catch (e) {
          throw saveErr(route, e, c.gp.ui.dict());
        }
        if (!alive()) return;
        hookToast(c, res, 'toastCreated');
      } else {
        const route = 'PUT /api/admin/bots/{id}';
        const patch = changedOf(body, r);
        if (Object.keys(patch).length > 0) {
          let res: BotSaveResp;
          try {
            res = await c.gp.api.call<BotSaveResp>(route, { path: { id: r.id }, body: patch });
          } catch (e) {
            throw saveErr(route, e, c.gp.ui.dict());
          }
          if (!alive()) return;
          hookToast(c, res, 'toastSaved');
        } else c.gp.ui.toast(str(c.gp.ui.dict(), 'toastSaved'));
      }
      void reloadCurrent(c);
    },
  };
}

async function openBotForm(ctx: AdminCtx, r: BotRow | null): Promise<void> {
  const alive = sessionGuard(ctx);
  const agents = await agentChoices(ctx);
  if (!alive()) return;
  openForm(ctx, botForm(ctx, r, agents));
}

// ---- 行内动作 ----

// 停用 / 启用：只发 {status}（后端动态声明 toggle_bot，SP7 TD-706）
async function toggleBot(ctx: AdminCtx, r: BotRow): Promise<void> {
  const alive = sessionGuard(ctx);
  let res: BotSaveResp;
  try {
    res = await ctx.gp.api.call<BotSaveResp>('PUT /api/admin/bots/{id}', { path: { id: r.id }, body: { status: r.status === 'active' ? 'disabled' : 'active' } });
  } catch (e) {
    if (!alive()) return;
    if (noPublicBase(e)) {
      ctx.gp.ui.toast(str(ctx.gp.ui.dict(), 'tgbErrNoPublicBase'));
      return;
    }
    if (isPrimaryErr(e)) {
      ctx.gp.ui.toast(str(ctx.gp.ui.dict(), 'tgbErrPrimary'));
      return;
    }
    throw e;
  }
  if (!alive()) return;
  hookToast(ctx, res, 'toastSaved');
  await reloadCurrent(ctx);
}

// 删除（Q-903）：确认文案 {name} · {username}；最后一个 active 平台 Bot → 1012 last_platform_bot
function deleteBot(ctx: AdminCtx, r: BotRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'delTitle'),
    text: str(t, 'delText'),
    label: `${r.name} · ${r.username}`,
    run: async (c) => {
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call('DELETE /api/admin/bots/{id}', { path: { id: r.id } });
      } catch (e) {
        if (!alive()) return;
        if (e instanceof ApiError && e.code === 1012 && e.data.reason === 'last_platform_bot') {
          c.gp.ui.toast(str(c.gp.ui.dict(), 'tgbErrLastPlatform'));
          return;
        }
        if (isPrimaryErr(e)) {
          c.gp.ui.toast(str(c.gp.ui.dict(), 'tgbErrPrimary'));
          return;
        }
        throw e;
      }
      if (!alive()) return;
      forgetBot(c.host, r.id);
      c.gp.ui.toast(str(c.gp.ui.dict(), 'toastBotDeleted'));
      await reloadCurrent(c);
    },
  });
}

// 设为主 Bot（0039）：仅非主的 active 平台 Bot 显示；页面内确认弹层说明影响范围 → POST set-primary（tgbot.edit，bot_set_primary）
export function canSetPrimary(r: BotRow): boolean {
  return r.owner_kind === 'platform' && r.status === 'active' && r.is_primary !== true;
}

function setPrimaryBot(ctx: AdminCtx, r: BotRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'tgbPrimaryTitle'),
    text: str(t, 'tgbPrimaryText'),
    label: `${r.name} · ${r.username}`,
    run: async (c) => {
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call<BotPrimaryResp>('POST /api/admin/bots/{id}/set-primary', { path: { id: r.id }, body: {} });
      } catch (e) {
        if (!alive()) return;
        if (e instanceof ApiError && e.code === 1012 && e.data.reason === 'not_active_platform') {
          c.gp.ui.toast(str(c.gp.ui.dict(), 'tgbErrPrimaryTarget'));
          await reloadCurrent(c);
          return;
        }
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(interpolate(str(c.gp.ui.dict(), 'tgbPrimaryDone'), { username: r.username }));
      await reloadCurrent(c);
    },
  });
}

export const PAGES_TGBOT: PageMap = {
  tgbot: {
    page: 'tgbot',
    i18n: I18N_TGBOT,
    rewrites: REWRITES_TGBOT,
    tabLabels: (t) => strArr(t, 'tabTgbot'),
    views: [
      defineView<BotRow>({
        key: 'list',
        source: { route: 'GET /api/admin/bots', tabParam: 'none' },
        echo: '/admin/bots',
        cols: [
          { label: 'cBotName' },
          { label: 'cBotId' },
          { label: 'cBotUser' },
          { label: 'cOwner', facet: 'owner', facetLabel: labelOf(ownerMap) },
          { label: 'cBotMode', facet: 'mode', facetLabel: labelOf(modeMap) },
          { label: 'cBotHook' },
          { label: 'cBotUsers' },
          { label: 'cCreated', sortKey: 'created_at' },
          { label: 'cStatus', facet: 'status', facetLabel: labelOf(statusMap) },
        ],
        cells: botCells,
        open: (c, r) => selectBot(c, r.id, r.username),
        rowActions: (): readonly ActionDef<BotRow>[] => [
          // 编辑配置：切页签 1（tgbot.view，页面可见即具备；不设权限门）
          { label: 'aSelect', run: (c, r) => selectBot(c, r.id, r.username) },
          { label: 'aEdit', need: TG_EDIT, run: (c, r) => openBotForm(c, r) },
          { label: 'stOff', need: TG_EDIT, danger: true, show: (r) => r.status === 'active', run: (c, r) => toggleBot(c, r) },
          { label: 'stOn', need: TG_EDIT, show: (r) => r.status !== 'active', run: (c, r) => toggleBot(c, r) },
          { label: 'aDelete', need: TG_EDIT, danger: true, run: (c, r) => deleteBot(c, r) },
          { label: 'tgbSetPrimary', need: TG_EDIT, show: canSetPrimary, run: (c, r) => setPrimaryBot(c, r) },
        ],
        stats: botStats,
        primary: { label: 'newBot', icon: '+ ', need: TG_EDIT, run: (c) => openBotForm(c, null) },
        csvName: 'bots',
        noExport: true, // 06 C-15：Bot 列表无导出（GET /bots 对 format 返 4220）
      }),
    ],
  },
};
